/**
 * Contact Service
 *
 * POST /api/public/contact (contract v1 §3.5). The backend stores the
 * message before emailing the owner, so a 201 means it was received even
 * if email delivery is delayed.
 */

import { post } from './apiClient';

export const CONTACT_MESSAGE_MIN_LENGTH = 10;
export const CONTACT_MESSAGE_MAX_LENGTH = 5000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Build the snake_case request body.
 * @param {object} formData
 * @returns {{name:string, email:string, phone:string|null, message:string, website:string}}
 */
export const buildContactPayload = (formData) => ({
  name: (formData.name || '').trim(),
  email: (formData.email || '').trim().toLowerCase(),
  phone: typeof formData.phone === 'string' && formData.phone.trim() ? formData.phone.trim() : null,
  message: (formData.message || '').trim(),
  website: typeof formData.website === 'string' ? formData.website : '',
});

/**
 * Submit the contact form.
 *
 * @param {object} formData
 * @param {string} formData.name
 * @param {string} formData.email
 * @param {string} formData.message - 10 to 5000 characters
 * @param {string} [formData.phone]
 * @param {string} [formData.website] - Honeypot; real visitors leave it empty
 * @returns {Promise<{received: boolean}>}
 * @throws {ApiError} 400 validation_error, 429 rate_limited (message user-facing)
 * @throws {Error} Client-side validation failure (message user-facing)
 */
export const sendContactForm = async (formData = {}) => {
  const payload = buildContactPayload(formData);

  const missing = ['name', 'email', 'message'].filter((field) => !payload[field]);
  if (missing.length) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }
  if (!EMAIL_RE.test(payload.email)) {
    throw new Error('Please enter a valid email address');
  }
  if (payload.message.length < CONTACT_MESSAGE_MIN_LENGTH) {
    throw new Error(`Message must be at least ${CONTACT_MESSAGE_MIN_LENGTH} characters long`);
  }
  if (payload.message.length > CONTACT_MESSAGE_MAX_LENGTH) {
    throw new Error(`Message must be at most ${CONTACT_MESSAGE_MAX_LENGTH} characters long`);
  }

  const data = await post('/api/public/contact', payload);
  return { received: data.received === true };
};
